// ════════════════════════════════════════════════════════════════════
// src/storeVisits/templateEditor.js  —  feature D, Phase 4 (v126.0)
//
// Admin editor for the DETAILED store-visit template. NARROW scope:
//   - edit section titles and question labels (keys NEVER change)
//   - add new questions (RAG / Comment / Photos) and new sections
//   - NO remove, NO rename-key, NO reorder
// Narrow is safe by construction: existing keys are immutable and added
// questions get fresh unique keys, so the key space can't churn.
//
// Reads  GET /api/visit-template   (active detailed template)
// Writes PUT /api/visit-template   ({ sections: [...] })
//
// Editing affects NEW visits only — submitted reports render against their
// own frozen template_snapshot, so history is never rewritten.
//
// Free vars (DB, document, sessionStorage, fetch) live in admin.html's inline
// scope; esbuild leaves them as bare references, per the module convention.
// ════════════════════════════════════════════════════════════════════

import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

function getToken() { return adminToken(); }   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function attr(s) { return escapeHtml(s).replace(/'/g, '&#39;'); }
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return String(iso); }
}

async function apiGet(pathStr) {
  const res = await fetch(getWorkerUrl() + pathStr, { headers: { 'Authorization': 'Bearer ' + getToken() } });
  if (!res.ok) { const t = await res.text().catch(function () { return ''; }); throw new Error(res.status + ': ' + t.slice(0, 160)); }
  return res.json();
}
async function apiPut(pathStr, payload) {
  const res = await fetch(getWorkerUrl() + pathStr, {
    method: 'PUT',
    headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { const t = await res.text().catch(function () { return ''; }); throw new Error(res.status + ': ' + t.slice(0, 160)); }
  return res.json();
}

// ── state ───────────────────────────────────────────────────────────
let _tpl = null;     // { id, label, sections, updated_at, updated_by }

const TYPE_LABEL = { rag: 'RAG rating', comment: 'Comment', photos: 'Photos', 'shift-embed': 'Live data' };
// Types an admin may ADD. shift-embed is intentionally excluded — those are
// wired to specific live-data widgets in code, not freely creatable.
const ADDABLE_TYPES = [['rag', 'RAG rating'], ['comment', 'Comment'], ['photos', 'Photos']];

// Stable, readable, unique-within-scope key from a label (e.g. "Are bins empty?" -> "binsEmpty").
function slugKey(label, existing) {
  const words = String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  let base = words.map(function (w, i) { return i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1); }).join('');
  if (!base) base = 'custom';
  let key = base, n = 2;
  while (existing.indexOf(key) !== -1) { key = base + n; n++; }
  return key;
}

const IN_STYLE = 'width:100%;box-sizing:border-box;padding:8px 10px;border:1px solid var(--border,#d1d5db);' +
  'border-radius:8px;font-size:14px;font-family:inherit;background:var(--card,#fff);color:var(--text,#111);';

// ── render ──────────────────────────────────────────────────────────
function root() { return document.getElementById('visit-template-root'); }

async function renderVisitTemplateEditor() {
  const el = root();
  if (!el) { console.warn('[templateEditor] #visit-template-root not found'); return; }
  el.innerHTML = '<div style="padding:24px 4px;color:var(--sub,#6b7280);font-size:14px;">Loading template…</div>';
  try {
    _tpl = await apiGet('/api/visit-template');
    draw();
  } catch (e) {
    el.innerHTML = '<div style="padding:24px 4px;color:var(--ui-bad);font-size:14px;">Couldn\u2019t load the template. ' +
      escapeHtml(e.message) + '</div>';
  }
}

function saveBar() {
  return '<div style="display:flex;align-items:center;gap:12px;margin:14px 0;">' +
    '<button type="button" class="btn btn-green tpl-save">Save template</button>' +
    '<span class="tpl-status" style="font-size:13px;"></span>' +
  '</div>';
}

function headerHtml() {
  const meta = (_tpl && _tpl.updated_at)
    ? 'Last edited ' + fmtDate(_tpl.updated_at) + (_tpl.updated_by ? ' by ' + escapeHtml(_tpl.updated_by) : '')
    : '';
  return '<div style="background:var(--info-bg);border:1px solid var(--learn-info-line);border-radius:10px;padding:12px 14px;margin-bottom:14px;font-size:13px;color:var(--info-deep);line-height:1.5;">' +
      'Edit question wording or add new checks for the <b>detailed</b> store visit. ' +
      '<b>Changes apply to new visits only</b> — reports already submitted keep the template they were filled against.' +
      (meta ? '<div style="margin-top:6px;color:var(--info-deep);font-size:12px;">' + meta + '</div>' : '') +
    '</div>' + saveBar();
}

function questionRowHtml(si, qi, q) {
  const badge = TYPE_LABEL[q.type] || q.type || '';
  return '<div style="display:flex;gap:8px;align-items:center;margin-bottom:7px;">' +
    '<span style="flex:0 0 auto;min-width:78px;text-align:center;padding:3px 8px;border-radius:6px;background:var(--bg,#f2f2f7);' +
      'font-size:11px;font-weight:700;color:var(--sub,#6b7280);">' + escapeHtml(badge) + '</span>' +
    '<input class="tpl-q-label" data-si="' + si + '" data-qi="' + qi + '" value="' + attr(q.label || '') + '" ' +
      'style="' + IN_STYLE + '">' +
  '</div>';
}

function addQuestionHtml(si) {
  return '<div style="display:flex;gap:8px;align-items:center;margin-top:10px;padding-top:10px;border-top:1px dashed var(--border,#e5e7eb);">' +
    '<input class="tpl-newq-label" data-si="' + si + '" placeholder="Add a question\u2026" style="' + IN_STYLE + '">' +
    '<select class="tpl-newq-type" data-si="' + si + '" style="flex:0 0 auto;padding:8px;border:1px solid var(--border,#d1d5db);border-radius:8px;font-size:13px;background:var(--card,#fff);">' +
      ADDABLE_TYPES.map(function (t) { return '<option value="' + t[0] + '">' + t[1] + '</option>'; }).join('') +
    '</select>' +
    '<button type="button" class="btn btn-outline tpl-addq" data-si="' + si + '" style="flex:0 0 auto;white-space:nowrap;">+ Add</button>' +
  '</div>';
}

function sectionHtml(section, si) {
  const qs = section.questions || [];
  return '<div style="border:1px solid var(--border,#e5e7eb);border-radius:10px;padding:14px;margin-bottom:14px;background:var(--card,#fff);">' +
    '<label style="display:block;font-size:11px;font-weight:700;color:var(--sub,#6b7280);text-transform:uppercase;letter-spacing:.04em;margin-bottom:5px;">Section title</label>' +
    '<input class="tpl-sec-title" data-si="' + si + '" value="' + attr(section.title || '') + '" style="' + IN_STYLE + 'font-weight:700;margin-bottom:12px;">' +
    qs.map(function (q, qi) { return questionRowHtml(si, qi, q); }).join('') +
    addQuestionHtml(si) +
  '</div>';
}

function addSectionHtml() {
  return '<div style="display:flex;gap:8px;align-items:center;border:1px dashed var(--border,#d1d5db);border-radius:10px;padding:14px;margin-bottom:14px;">' +
    '<input class="tpl-newsec-title" placeholder="Add a new section\u2026" style="' + IN_STYLE + '">' +
    '<button type="button" class="btn btn-outline tpl-addsec" style="flex:0 0 auto;white-space:nowrap;">+ Add section</button>' +
  '</div>';
}

function draw() {
  const el = root();
  if (!el) return;
  const sections = (_tpl && _tpl.sections) || [];
  el.innerHTML = headerHtml() +
    sections.map(function (s, si) { return sectionHtml(s, si); }).join('') +
    addSectionHtml() +
    saveBar();
  wire();
}

// ── wire ────────────────────────────────────────────────────────────
function wire() {
  const el = root();
  el.querySelectorAll('.tpl-sec-title').forEach(function (inp) {
    inp.addEventListener('input', function () { _tpl.sections[+inp.dataset.si].title = inp.value; });
  });
  el.querySelectorAll('.tpl-q-label').forEach(function (inp) {
    inp.addEventListener('input', function () {
      _tpl.sections[+inp.dataset.si].questions[+inp.dataset.qi].label = inp.value;
    });
  });
  el.querySelectorAll('.tpl-addq').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const si = +btn.dataset.si;
      const labelEl = el.querySelector('.tpl-newq-label[data-si="' + si + '"]');
      const typeEl = el.querySelector('.tpl-newq-type[data-si="' + si + '"]');
      const label = (labelEl.value || '').trim();
      if (!label) { labelEl.focus(); return; }
      const sec = _tpl.sections[si];
      sec.questions = sec.questions || [];
      const key = slugKey(label, sec.questions.map(function (q) { return q.key; }));
      sec.questions.push({ key: key, type: typeEl.value, label: label });
      draw();
    });
  });
  const addSec = el.querySelector('.tpl-addsec');
  if (addSec) addSec.addEventListener('click', function () {
    const titleEl = el.querySelector('.tpl-newsec-title');
    const title = (titleEl.value || '').trim();
    if (!title) { titleEl.focus(); return; }
    _tpl.sections = _tpl.sections || [];
    const key = slugKey(title, _tpl.sections.map(function (s) { return s.key; }));
    _tpl.sections.push({ key: key, title: title, questions: [] });
    draw();
  });
  el.querySelectorAll('.tpl-save').forEach(function (btn) { btn.addEventListener('click', save); });
}

function setStatus(msg, isErr) {
  root().querySelectorAll('.tpl-status').forEach(function (s) {
    s.textContent = msg; s.style.color = isErr ? 'var(--ui-bad)' : 'var(--ok)';
  });
}

async function save() {
  const sections = (_tpl && _tpl.sections) || [];
  // Validate: no empty section titles or question labels would ship a blank form line.
  for (let i = 0; i < sections.length; i++) {
    if (!String(sections[i].title || '').trim()) { setStatus('Every section needs a title.', true); return; }
    const qs = sections[i].questions || [];
    for (let j = 0; j < qs.length; j++) {
      if (!String(qs[j].label || '').trim()) {
        setStatus('Every question needs a label — check "' + (sections[i].title || 'a section') + '".', true);
        return;
      }
    }
  }
  setStatus('Saving\u2026');
  try {
    const saved = await apiPut('/api/visit-template', { sections: sections });
    if (saved && saved.updated_at) { _tpl.updated_at = saved.updated_at; _tpl.updated_by = saved.updated_by; }
    draw();
    setStatus('Saved. New visits will use this template.');
  } catch (e) {
    setStatus('Couldn\u2019t save: ' + (e && e.message ? e.message : e), true);
  }
}

// Window re-exposure (per ELC Shift module convention)
window.renderVisitTemplateEditor = renderVisitTemplateEditor;

export { renderVisitTemplateEditor };
